import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import WP_API from "../config/api";
import { decodeEntities } from "../utils/html";
import "./Navbar.css";

const CACHE_KEY = "novamas:categories:v1";

// Refresh at most once per page load, started at module scope — i.e. before
// React renders — instead of inside an effect that only runs after the first
// paint. Otherwise the header paints with just the static links and the
// WordPress categories pop in ~0.9s later (visible layout shift).
const fetchCategories = (() => {
	let promise = null;
	return () => {
		if (!promise) {
			promise = fetch(
				`${WP_API.categories}?per_page=100&orderby=name&order=asc`
			)
				.then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
				.then((list) => {
					const cats = (Array.isArray(list) ? list : []).filter(
						(cat) => cat.slug !== "uncategorized"
					);
					try {
						localStorage.setItem(CACHE_KEY, JSON.stringify(cats));
					} catch (e) {
						/* private mode / quota — the cache is only an optimisation */
					}
					return cats;
				})
				.catch(() => null); // network failed: keep the snapshot we have
		}
		return promise;
	};
})();
fetchCategories(); // kick off immediately, do not wait for mount

const readSnapshot = () => {
	try {
		const raw = localStorage.getItem(CACHE_KEY);
		if (raw) return JSON.parse(raw);
	} catch (e) {
		/* ignore */
	}
	// Injected into index.html at build time by scripts/inject-categories.mjs,
	// so even a first visit paints with the categories already in place.
	if (Array.isArray(window.__NOVAMAS_CATEGORIES__)) {
		return window.__NOVAMAS_CATEGORIES__;
	}
	return [];
};

const Navbar = ({ currentPath }) => {
	const [isScrolled, setIsScrolled] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	// Seeded synchronously so the very first paint already has the right links
	const [categories, setCategories] = useState(readSnapshot);
	const location = useLocation();
	const headerRef = useRef(null);

	// Blog categories come straight from WordPress, so a new category shows up
	// in the menu without touching the React source again.
	useEffect(() => {
		let alive = true;
		fetchCategories().then((cats) => {
			if (alive && cats) setCategories(cats);
		});
		return () => {
			alive = false;
		};
	}, []);

	// The category row wraps onto extra lines as categories are added, so the
	// header is not a fixed height. Publish the real height for the hero and
	// the inner pages, which sit underneath this fixed header.
	useLayoutEffect(() => {
		const header = headerRef.current;
		if (!header) return;
		const publish = () =>
			document.documentElement.style.setProperty(
				"--header-h",
				`${header.offsetHeight}px`
			);
		publish();
		window.addEventListener("resize", publish);
		let ro;
		if (typeof ResizeObserver !== "undefined") {
			ro = new ResizeObserver(publish);
			ro.observe(header);
		}
		return () => {
			window.removeEventListener("resize", publish);
			if (ro) ro.disconnect();
		};
	}, [categories]);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 50);
		};
		window.addEventListener("scroll", handleScroll);

		// Set initial scroll state
		handleScroll();

		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const isActive = (cat) =>
		location.pathname === "/blogovi" &&
		new URLSearchParams(location.search).get("category") === String(cat.id);

	// Determine if this is the homepage
	const isHomepage = location.pathname === "/" || !location.pathname;

	return (
		<header
			ref={headerRef}
			className={`header ${isScrolled ? "scrolled" : ""} ${
				!isHomepage ? "header-solid" : ""
			}`}
		>
			<div className="container">
				<nav className="navbar">
					<Link to="/" className="logo">
						<img src="/logo.webp" alt="NovamaS" className="logo-dark" />
						<img src="/white_big_logo.png" alt="" aria-hidden="true" className="logo-light" />
					</Link>

					{/* Everything that stacks into the slide-down panel on phones.
					    On desktop this wrapper is display:contents, so the two
					    groups below take part in the header's own layout. */}
					<div
						className={`nav-links ${mobileMenuOpen ? "active" : ""}`}
						aria-label="Glavna navigacija"
					>
						<div className="nav-primary">
							<Link
								to="/o-nama"
								className={`nav-link ${
									location.pathname === "/o-nama" ? "active" : ""
								}`}
							>
								O nama
							</Link>
							<Link
								to="/kontakt"
								className={`nav-link ${
									location.pathname === "/kontakt" ? "active" : ""
								}`}
							>
								Kontakt
							</Link>
						</div>

						{/* Second row, full width, centred — wraps to more lines
						    instead of ever crowding the logo. */}
						<div className="nav-categories">
							{categories.map((cat) => (
								<Link
									key={cat.id}
									to={`/blogovi?category=${cat.id}`}
									className={`nav-link ${isActive(cat) ? "active" : ""}`}
								>
									{decodeEntities(cat.name)}
								</Link>
							))}
						</div>
					</div>

					<div className="nav-right">
						<button
							className="menu-toggle"
							onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
							aria-label={mobileMenuOpen ? "Zatvori meni" : "Otvori meni"}
						>
							{mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
						</button>
					</div>
				</nav>
			</div>
		</header>
	);
};

export default Navbar;
